import { useQuery, useReactiveVar } from '@apollo/client';
import { READ_CORPORATE_ACCOUNT_PREFERENCE } from '@queries/readCorporateAccountPreference';
import { Headline3, List, Spinner } from 'aimo-ui';
import PaymentMethodItem from 'components/account/paymentMethods/PaymentMethodItem';
import AddPaymentMethod from 'components/payment/AddPaymentMethod';
import CorporateAccountToggle from 'components/payment/CorporateAccountToggle';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useExternalPaymentMethods, {
  ExternalPaymentWrapper,
} from 'hooks/useExternalPaymentMethods';
import { useVerifyWalletToken } from 'hooks/useVerifyWalletToken';
import useVerifyingCardsPoll from 'hooks/useVerifyingCardsPoll';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentStore } from 'stores/paymentStore';
import { featureCountryCode } from 'states/persistInStorage';
import { useWalletTokenVerifyStore } from 'stores/walletTokenVerifyStore';
import {
  ScreenContainer,
  SheetDetailsWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { PaymentDataProps } from 'types/PaymentData';
import {
  Card,
  CompanyBenefit,
  CountryCode,
  Maybe,
  PaymentOption,
  Query,
} from 'types/generatedSchemaTypes';
import { SelectedPaymentCardStateProps } from 'types/states';
import { isWeb } from 'utils/deviceUtils';
import {
  isMobilePayEnabledForCountry,
  parseFeatureFlagObject,
  showFeatureFlag,
  TypeDictionary,
} from 'utils/featureFlagUtils';
import { enrichPaymentOptionsWithNames } from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';

type PaymentMethodsFooterProps = {
  showCompanyBenefits: boolean;
  companyBenefits: Maybe<CompanyBenefit[]> | undefined;
  showPaymentOptions: boolean;
  corporateAccountPaymentOptions: PaymentOption[];
  isCADefault: boolean;
  onToggleCompleted: (value: boolean) => void;
};

const paymentData: PaymentDataProps[] = [
  {
    title: 'payment.addPaymentCard',
    icon: 'plus',
    action: 'addCardModal',
    testID: 'add-credit-card-button',
  },
];

const AddPaymentMethodButton = () => (
  <AddPaymentMethod paymentData={paymentData} />
);

const PaymentMethodsFooter: React.FC<PaymentMethodsFooterProps> = ({
  showCompanyBenefits,
  companyBenefits,
  showPaymentOptions,
  corporateAccountPaymentOptions,
  isCADefault,
  onToggleCompleted,
}) => {
  const {
    flags: { account_payment_ca_as_default_toggle_display },
  } = useContext(FeatureFlagsContext);

  const appCountryCode = useReactiveVar(featureCountryCode);

  useVerifyWalletToken();

  // Determine if the toggle should be displayed based on the feature flag configuration
  const isToggleVisible: boolean = useMemo(() => {
    const toggleConfig = parseFeatureFlagObject<
      TypeDictionary<CountryCode, boolean>
    >(
      account_payment_ca_as_default_toggle_display,
      'account_payment_ca_as_default_toggle_display'
    );

    return appCountryCode && toggleConfig?.[appCountryCode] !== undefined
      ? toggleConfig[appCountryCode]
      : false;
  }, [account_payment_ca_as_default_toggle_display, appCountryCode]);

  const hasCompanyBenefits = showCompanyBenefits && companyBenefits?.length > 0;
  const hasCorporateAccountPaymentOption =
    showPaymentOptions && corporateAccountPaymentOptions?.length > 0;
  const shouldRenderCorporateAccountToggle =
    isToggleVisible && (hasCompanyBenefits || hasCorporateAccountPaymentOption);

  return (
    <>
      {shouldRenderCorporateAccountToggle && (
        <CorporateAccountToggle
          isCADefault={isCADefault}
          onToggleCompleted={onToggleCompleted}
        />
      )}
      <AddPaymentMethod paymentData={paymentData} />
    </>
  );
};

type PaymentListItem =
  | Maybe<Card>
  | Maybe<CompanyBenefit>
  | ExternalPaymentWrapper
  | PaymentOption;

const buildPaymentListData = ({
  mobilePayOptions,
  walletTokenPendingVerification,
  paymentCards,
  selectedCard,
  showCompanyBenefits,
  companyBenefits,
  externalPaymentMethods,
  showPaymentOptions,
  corporateAccountPaymentOptions,
}: {
  mobilePayOptions: PaymentOption[];
  walletTokenPendingVerification: unknown;
  paymentCards: Maybe<Maybe<Card>[]> | undefined;
  selectedCard: SelectedPaymentCardStateProps;
  showCompanyBenefits: boolean;
  companyBenefits: Maybe<CompanyBenefit[]> | undefined;
  externalPaymentMethods: ExternalPaymentWrapper[] | null;
  showPaymentOptions: boolean;
  corporateAccountPaymentOptions: PaymentOption[];
}): PaymentListItem[] => {
  let data: PaymentListItem[] = [];

  if (mobilePayOptions.length > 0) {
    data = data.concat(mobilePayOptions);
  } else if (walletTokenPendingVerification) {
    data = data.concat({
      __typename: 'PaymentOption' as const,
      method: 'MOBILEPAY',
      optionId: 'mobilepay-verifying',
      isDefaultTokenizedPaymentOption: false,
    } as PaymentOption);
  }

  if (paymentCards) {
    data = data.concat(paymentCards);
  }
  if (
    selectedCard.status === 'Verifying' &&
    !paymentCards?.find((card) => card?.cardId === selectedCard.cardId)
  ) {
    data = data.concat({ ...selectedCard, __typename: 'Card' } as Card);
  }

  if (showCompanyBenefits && companyBenefits) {
    data = data.concat(companyBenefits);
  }
  if (externalPaymentMethods) {
    data = data.concat(externalPaymentMethods);
  }
  if (showPaymentOptions && corporateAccountPaymentOptions.length > 0) {
    data = data.concat(corporateAccountPaymentOptions);
  }

  return data;
};

const MyPaymentMethods: React.FC = () => {
  const { t } = useTranslation();
  const {
    customer: { paymentCards, companyBenefits, paymentOptions, contactEmail },
    loading: customerLoading,
    refetchCustomer,
  } = useContext(AccountContext);
  const selectedCard = usePaymentStore((s) => s.selectedPaymentCard);

  // While any card is verifying (an Adyen card-add is optimistically shown before
  // its recurring.token webhook confirms it active), poll the customer so the list
  // reconciles VERIFYING -> ACTIVE on its own.
  useVerifyingCardsPoll(paymentCards, refetchCustomer);

  const appCountry = useReactiveVar(featureCountryCode);
  const {
    flags: {
      company_benefits,
      ca_with_license_plates,
      enable_mobilepay_by_country,
    },
  } = useContext(FeatureFlagsContext);
  const showMobilePay = isMobilePayEnabledForCountry(
    enable_mobilepay_by_country,
    appCountry,
    contactEmail
  );
  const { externalPaymentMethods } = useExternalPaymentMethods();
  const walletTokenPendingVerification = useWalletTokenVerifyStore(
    (state) => state.walletTokenPendingVerification
  );

  const showPaymentOptions = showFeatureFlag(ca_with_license_plates);
  const showCompanyBenefits =
    showFeatureFlag(company_benefits) && !showPaymentOptions;

  const {
    data: corporateAccountPreferenceData,
    loading: corporateAccountLoading,
  } = useQuery<Query>(READ_CORPORATE_ACCOUNT_PREFERENCE, {
    fetchPolicy: 'cache-and-network',
  });

  const [isCADefault, setIsCADefault] = useState(false);

  useEffect(() => {
    if (!corporateAccountLoading) {
      setIsCADefault(
        corporateAccountPreferenceData?.readCorporateAccountPreference ?? false
      );
    }
  }, [corporateAccountLoading, corporateAccountPreferenceData]);

  const handleToggleCompleted = (value: boolean) => {
    setIsCADefault(value);
  };

  const corporateAccountPaymentOptions = useMemo(() => {
    if (!showPaymentOptions) return [];

    const corporateOptions = (paymentOptions || []).filter(
      (option) => option.method === 'CORPORATE_ACCOUNT'
    );
    return enrichPaymentOptionsWithNames(corporateOptions, companyBenefits).map(
      (option) => ({
        ...option,
        __typename: 'PaymentOption' as const,
      })
    );
  }, [paymentOptions, companyBenefits, showPaymentOptions]);

  const mobilePayOptions = (paymentOptions || []).filter(
    (option) => option.method === 'MOBILEPAY' && showMobilePay
  );

  const data = buildPaymentListData({
    mobilePayOptions,
    walletTokenPendingVerification,
    paymentCards,
    selectedCard,
    showCompanyBenefits,
    companyBenefits,
    externalPaymentMethods,
    showPaymentOptions,
    corporateAccountPaymentOptions,
  });

  const paymentFooterComponent = () => {
    return (
      <PaymentMethodsFooter
        showCompanyBenefits={showCompanyBenefits}
        companyBenefits={companyBenefits}
        showPaymentOptions={showPaymentOptions}
        corporateAccountPaymentOptions={corporateAccountPaymentOptions}
        isCADefault={isCADefault}
        onToggleCompleted={handleToggleCompleted}
      />
    );
  };

  return (
    <ScreenContainer mt={isWeb ? 190 : 0}>
      <SheetDetailsWrapper pt={isWeb ? 60 : 30} pb={20}>
        {customerLoading ? (
          <StyledWrapper center>
            <Spinner />
          </StyledWrapper>
        ) : (
          <>
            <StyledWrapper mb={10}>
              <Headline3 {...testIdentifiers('debit-credit-cards-title')}>
                {t('paymentMethods.debitCreditCards')}
              </Headline3>
            </StyledWrapper>
            {data ? (
              <List
                listData={data}
                listElement={PaymentMethodItem}
                listFooter={paymentFooterComponent}
              />
            ) : (
              AddPaymentMethodButton
            )}
          </>
        )}
      </SheetDetailsWrapper>
    </ScreenContainer>
  );
};

export default MyPaymentMethods;
